import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { adminAuth, adminConfig } from '../../api/adminClient'
import ConfirmModal from '../../components/admin/ConfirmModal'

export default function AdminSettings() {
  const { t } = useTranslation('portal')
  const queryClient = useQueryClient()
  const [setupCode, setSetupCode] = useState('')
  const [disableConfirm, setDisableConfirm] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [removeDomain, setRemoveDomain] = useState<{ id: string; domain: string } | null>(null)
  const [defaultFeesForm, setDefaultFeesForm] = useState<{
    bank_fee_percent: string
    bank_fee_fixed: string
    platform_fee_percent: string
    platform_fee_fixed: string
  }>({ bank_fee_percent: '', bank_fee_fixed: '', platform_fee_percent: '', platform_fee_fixed: '' })
  const [paymentProviderForm, setPaymentProviderForm] = useState<{
    provider: string
    monri_api_key: string
    monri_api_secret: string
  }>({ provider: 'dummy', monri_api_key: '', monri_api_secret: '' })
  const [paymentProviderValidationError, setPaymentProviderValidationError] = useState('')

  const { data: twoFaStatus } = useQuery({
    queryKey: ['admin', '2fa-status'],
    queryFn: async () => (await adminAuth.twoFaStatus()).data,
  })
  const { data: defaultFees } = useQuery({
    queryKey: ['admin', 'config', 'fees-default'],
    queryFn: async () => (await adminConfig.getDefaultFees()).data,
  })
  const { data: systemSettings } = useQuery({
    queryKey: ['admin', 'config', 'settings'],
    queryFn: async () => (await adminConfig.getSettings()).data,
  })
  const { data: verificationDomains } = useQuery({
    queryKey: ['admin', 'config', 'invoice-verification-domains'],
    queryFn: async () => (await adminConfig.listInvoiceVerificationDomains()).data,
  })
  const { data: paymentProvider } = useQuery({
    queryKey: ['admin', 'config', 'payment-provider'],
    queryFn: async () => (await adminConfig.getPaymentProvider()).data,
  })

  const setupMutation = useMutation({
    mutationFn: () => adminAuth.twoFaSetup(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', '2fa-status'] }),
  })
  const verifyMutation = useMutation({
    mutationFn: (code: string) => adminAuth.twoFaVerify(code),
    onSuccess: () => {
      setSetupCode('')
      queryClient.invalidateQueries({ queryKey: ['admin', '2fa-status'] })
    },
  })
  const disableMutation = useMutation({
    mutationFn: () => adminAuth.twoFaDisable(),
    onSuccess: () => {
      setDisableConfirm(false)
      queryClient.invalidateQueries({ queryKey: ['admin', '2fa-status'] })
    },
  })
  const addDomainMutation = useMutation({
    mutationFn: (domain: string) => adminConfig.addInvoiceVerificationDomain(domain),
    onSuccess: () => {
      setNewDomain('')
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'invoice-verification-domains'] })
    },
  })
  const removeDomainMutation = useMutation({
    mutationFn: (id: string) => adminConfig.removeInvoiceVerificationDomain(id),
    onSuccess: () => {
      setRemoveDomain(null)
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'invoice-verification-domains'] })
    },
  })
  const updateDefaultFeesMutation = useMutation({
    mutationFn: (data: { bank_fee_percent: number; bank_fee_fixed: number; platform_fee_percent: number; platform_fee_fixed: number }) =>
      adminConfig.updateDefaultFees(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'fees-default'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'fees'] })
    },
  })
  const updatePaymentProviderMutation = useMutation({
    mutationFn: (data: { provider: string; monri_api_key?: string; monri_api_secret?: string }) =>
      adminConfig.updatePaymentProvider(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'payment-provider'] })
    },
  })
  const updateSettingsMutation = useMutation({
    mutationFn: (data: { allow_duplicate_receipt_payment: boolean }) =>
      adminConfig.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'settings'] })
    },
  })

  const enabled = twoFaStatus?.enabled ?? false
  const domains = verificationDomains?.items ?? []
  useEffect(() => {
    if (paymentProvider) {
      setPaymentProviderForm((s) => ({ ...s, provider: paymentProvider.provider || 'dummy' }))
    }
  }, [paymentProvider?.provider])

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('admin.settings.title')}</h1>

      <section className="rounded-xl bg-white border border-gray-200 p-6 mb-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-2">{t('admin.settings.twoFaTitle')}</h2>
        <p className="text-gray-500 text-sm mb-4">
          {t('admin.settings.status')}: <span className={enabled ? 'text-emerald-600' : 'text-amber-600'}>{enabled ? t('admin.settings.enabled') : t('admin.settings.disabled')}</span>
        </p>
        {!enabled && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setupMutation.mutate()}
              disabled={setupMutation.isPending}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-white text-sm hover:bg-emerald-500 disabled:opacity-50"
            >
              {setupMutation.isPending ? '…' : t('admin.settings.enable2fa')}
            </button>
            {setupMutation.isSuccess && (setupMutation.data as { data?: { secret?: string } })?.data && (
              <div className="mt-4 p-4 rounded-lg bg-gray-50 border border-gray-200">
                <p className="text-gray-600 text-sm mb-2">{t('admin.settings.scanAuthenticatorMessage')}</p>
                <p className="font-mono text-xs text-gray-500 break-all mb-3">{t('admin.settings.secretLabel')} {(setupMutation.data as { data?: { secret?: string } }).data?.secret}</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={setupCode}
                    onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ''))}
                    className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900 w-24 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={() => verifyMutation.mutate(setupCode)}
                    disabled={setupCode.length !== 6 || verifyMutation.isPending}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-white text-sm disabled:opacity-50"
                  >
                    {t('admin.settings.verifyAndEnable')}
                  </button>
                </div>
                {verifyMutation.isError && (
                  <p className="mt-2 text-sm text-red-600">{t('admin.settings.invalidCode')}</p>
                )}
              </div>
            )}
          </div>
        )}
        {enabled && (
          <button
            type="button"
            onClick={() => setDisableConfirm(true)}
            className="rounded-lg border border-red-300 text-red-600 px-4 py-2 text-sm hover:bg-red-50"
          >
            {t('admin.settings.disable2fa')}
          </button>
        )}
      </section>

      <section className="rounded-xl bg-white border border-gray-200 p-6 mb-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-2">{t('admin.settings.defaultFeeStructureTitle')}</h2>
        <p className="text-gray-500 text-sm mb-4">{t('admin.settings.defaultFeeStructureDesc')}</p>
        {defaultFees && (
          <form
            className="space-y-4 max-w-md"
            onSubmit={(e) => {
              e.preventDefault()
              // Use form values if set, otherwise fall back to displayed defaultFees (so Save works without editing)
              const bPct = parseFloat(defaultFeesForm.bank_fee_percent || String(defaultFees.bank_fee_percent ?? ''))
              const bFix = parseFloat(defaultFeesForm.bank_fee_fixed || String(defaultFees.bank_fee_fixed ?? ''))
              const pPct = parseFloat(defaultFeesForm.platform_fee_percent || String(defaultFees.platform_fee_percent ?? ''))
              const pFix = parseFloat(defaultFeesForm.platform_fee_fixed || String(defaultFees.platform_fee_fixed ?? ''))
              if (!Number.isFinite(bPct) || !Number.isFinite(bFix) || !Number.isFinite(pPct) || !Number.isFinite(pFix)) return
              updateDefaultFeesMutation.mutate({
                bank_fee_percent: bPct,
                bank_fee_fixed: bFix,
                platform_fee_percent: pPct,
                platform_fee_fixed: pFix,
              })
            }}
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.settings.bankFeePercent')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={defaultFeesForm.bank_fee_percent || (defaultFees.bank_fee_percent ?? '')}
                onChange={(e) => setDefaultFeesForm((s) => ({ ...s, bank_fee_percent: e.target.value }))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-gray-900 w-32 focus:ring-2 focus:ring-emerald-500"
              />
              <span className="ml-2 text-gray-500 text-sm">%</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.settings.bankFeeFixed')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={defaultFeesForm.bank_fee_fixed || (defaultFees.bank_fee_fixed ?? '')}
                onChange={(e) => setDefaultFeesForm((s) => ({ ...s, bank_fee_fixed: e.target.value }))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-gray-900 w-32 focus:ring-2 focus:ring-emerald-500"
              />
              <span className="ml-2 text-gray-500 text-sm">BAM</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.settings.platformFeePercent')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={defaultFeesForm.platform_fee_percent || (defaultFees.platform_fee_percent ?? '')}
                onChange={(e) => setDefaultFeesForm((s) => ({ ...s, platform_fee_percent: e.target.value }))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-gray-900 w-32 focus:ring-2 focus:ring-emerald-500"
              />
              <span className="ml-2 text-gray-500 text-sm">%</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.settings.platformFeeFixed')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={defaultFeesForm.platform_fee_fixed || (defaultFees.platform_fee_fixed ?? '')}
                onChange={(e) => setDefaultFeesForm((s) => ({ ...s, platform_fee_fixed: e.target.value }))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-gray-900 w-32 focus:ring-2 focus:ring-emerald-500"
              />
              <span className="ml-2 text-gray-500 text-sm">BAM</span>
            </div>
            <button
              type="submit"
              disabled={updateDefaultFeesMutation.isPending}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-white text-sm hover:bg-emerald-500 disabled:opacity-50"
            >
              {updateDefaultFeesMutation.isPending ? '…' : t('admin.settings.saveDefaultFees')}
            </button>
            {updateDefaultFeesMutation.isError && (
              <p className="text-sm text-red-600">
                {(updateDefaultFeesMutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? t('admin.settings.saveDefaultFeesError')}
              </p>
            )}
          </form>
        )}
      </section>

      <section className="rounded-xl bg-white border border-gray-200 p-6 mb-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-2">{t('admin.settings.paymentProviderTitle')}</h2>
        <p className="text-gray-500 text-sm mb-4">{t('admin.settings.paymentProviderDesc')}</p>
        {paymentProvider && (
          <form
            className="space-y-4 max-w-md"
            onSubmit={(e) => {
              e.preventDefault()
              const provider = paymentProviderForm.provider || 'dummy'
              if (provider === 'monri' || provider === 'monri_test') {
                const key = paymentProviderForm.monri_api_key.trim()
                const secret = paymentProviderForm.monri_api_secret.trim()
                const configured = paymentProvider.monri_configured
                if (!configured && (!key || !secret)) {
                  setPaymentProviderValidationError(t('admin.settings.monriKeySecretRequired'))
                  return
                }
                setPaymentProviderValidationError('')
                const payload: { provider: string; monri_api_key?: string; monri_api_secret?: string } = { provider }
                if (key) payload.monri_api_key = key
                if (secret && secret !== '••••••••') payload.monri_api_secret = secret
                updatePaymentProviderMutation.mutate(payload)
              } else {
                setPaymentProviderValidationError('')
                updatePaymentProviderMutation.mutate({ provider: 'dummy' })
              }
            }}
          >
            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium text-gray-700">{t('admin.settings.paymentProviderLabel')}</label>
              <div className="flex gap-4">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="paymentProvider"
                    value="dummy"
                    checked={(paymentProviderForm.provider || paymentProvider.provider) === 'dummy'}
                    onChange={() => setPaymentProviderForm((s) => ({ ...s, provider: 'dummy' }))}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span>{t('admin.settings.paymentProviderDummy')}</span>
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="paymentProvider"
                    value="monri_test"
                    checked={(paymentProviderForm.provider || paymentProvider.provider) === 'monri_test'}
                    onChange={() => setPaymentProviderForm((s) => ({ ...s, provider: 'monri_test' }))}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span>{t('admin.settings.paymentProviderMonriTest')}</span>
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="paymentProvider"
                    value="monri"
                    checked={(paymentProviderForm.provider || paymentProvider.provider) === 'monri'}
                    onChange={() => setPaymentProviderForm((s) => ({ ...s, provider: 'monri' }))}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span>{t('admin.settings.paymentProviderMonri')}</span>
                </label>
              </div>
            </div>
            {((paymentProviderForm.provider || paymentProvider.provider) === 'monri' || (paymentProviderForm.provider || paymentProvider.provider) === 'monri_test') && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.settings.monriApiSecret')}</label>
                  <input
                    type="password"
                    value={paymentProviderForm.monri_api_secret}
                    onChange={(e) => setPaymentProviderForm((s) => ({ ...s, monri_api_secret: e.target.value }))}
                    placeholder={paymentProvider.monri_configured ? '••••••••' : undefined}
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-1p-ignore
                    className="rounded-lg border border-gray-300 px-3 py-2 text-gray-900 w-full max-w-md focus:ring-2 focus:ring-emerald-500"
                  />
                  {paymentProvider.monri_configured && (
                    <p className="text-gray-500 text-xs mt-1">{t('admin.settings.monriKeySecretHint')}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.settings.monriApiKey')}</label>
                  <input
                    type="text"
                    value={paymentProviderForm.monri_api_key}
                    onChange={(e) => setPaymentProviderForm((s) => ({ ...s, monri_api_key: e.target.value }))}
                    placeholder={paymentProvider.monri_configured ? '••••••••' : undefined}
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore
                    className="rounded-lg border border-gray-300 px-3 py-2 text-gray-900 w-full max-w-md focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </>
            )}
            <button
              type="submit"
              disabled={updatePaymentProviderMutation.isPending}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-white text-sm hover:bg-emerald-500 disabled:opacity-50"
            >
              {updatePaymentProviderMutation.isPending ? '…' : t('admin.settings.savePaymentProvider')}
            </button>
            {paymentProviderValidationError && (
              <p className="text-sm text-amber-600">{paymentProviderValidationError}</p>
            )}
            {updatePaymentProviderMutation.isError && (
              <p className="text-sm text-red-600">
                {(updatePaymentProviderMutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? t('admin.settings.savePaymentProviderError')}
              </p>
            )}
          </form>
        )}
      </section>

      <section className="rounded-xl bg-white border border-gray-200 p-6 mb-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-2">{t('admin.settings.invoiceVerificationDomainsTitle')}</h2>
        <p className="text-gray-500 text-sm mb-4">{t('admin.settings.invoiceVerificationDomainsDesc')}</p>
        <div className="flex flex-wrap gap-2 mb-4">
          <input
            type="text"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder={t('admin.settings.domainPlaceholder')}
            className="rounded-lg border border-gray-300 px-3 py-2 text-gray-900 w-64 max-w-full focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
          <button
            type="button"
            onClick={() => newDomain.trim() && addDomainMutation.mutate(newDomain.trim())}
            disabled={!newDomain.trim() || addDomainMutation.isPending}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-white text-sm hover:bg-emerald-500 disabled:opacity-50"
          >
            {addDomainMutation.isPending ? '…' : t('admin.settings.addDomain')}
          </button>
        </div>
        {addDomainMutation.isError && (
          <p className="text-sm text-red-600 mb-2">
            {(addDomainMutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to add domain'}
          </p>
        )}
        {domains.length === 0 ? (
          <p className="text-gray-500 text-sm">{t('admin.settings.noDomains')}</p>
        ) : (
          <ul className="space-y-2">
            {domains.map((d) => (
              <li key={d.id} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 bg-gray-50">
                <span className="font-mono text-sm text-gray-900">{d.domain}</span>
                <button
                  type="button"
                  onClick={() => setRemoveDomain({ id: d.id, domain: d.domain })}
                  className="rounded border border-red-300 text-red-600 px-2 py-1 text-sm hover:bg-red-50"
                >
                  {t('admin.settings.removeDomain')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl bg-white border border-gray-200 p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-2">{t('admin.settings.systemSettingsTitle')}</h2>
        <p className="text-gray-500 text-sm mb-4">{t('admin.settings.systemSettingsDesc')}</p>
        {systemSettings && (
          <>
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={systemSettings.allow_duplicate_receipt_payment ?? false}
                  onChange={(e) => {
                    updateSettingsMutation.mutate({ allow_duplicate_receipt_payment: e.target.checked })
                  }}
                  disabled={updateSettingsMutation.isPending}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-gray-900">{t('admin.settings.allowDuplicateReceiptPayment')}</span>
              </label>
              <p className="text-gray-500 text-sm mt-1 ml-6">{t('admin.settings.allowDuplicateReceiptPaymentDesc')}</p>
              {updateSettingsMutation.isSuccess && (
                <p className="text-sm text-emerald-600 mt-1 ml-6">{t('admin.settings.saved')}</p>
              )}
              {updateSettingsMutation.isError && (
                <p className="text-sm text-red-600 mt-1 ml-6">
                  {(updateSettingsMutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? t('admin.settings.saveSystemSettingsError')}
                </p>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <dt className="text-gray-500">{t('admin.settings.payoutSchedule')}</dt>
              <dd className="text-gray-900">{systemSettings.payout_schedule}</dd>
              <dt className="text-gray-500">{t('admin.settings.currentPeriod')}</dt>
              <dd className="text-gray-900">{systemSettings.current_period_start ?? '–'} – {systemSettings.current_period_end ?? '–'}</dd>
              <dt className="text-gray-500">{t('admin.settings.nextPayoutDate')}</dt>
              <dd className="text-gray-900">{systemSettings.next_payout_date ?? '–'}</dd>
              <dt className="text-gray-500">{t('admin.settings.sessionTimeoutMin')}</dt>
              <dd className="text-gray-900">{systemSettings.session_timeout_minutes}</dd>
              <dt className="text-gray-500">{t('admin.settings.ipWhitelist')}</dt>
              <dd className="text-gray-900">{(systemSettings.ip_whitelist_enabled ?? systemSettings.ip_whitelist_configured) ? t('admin.settings.enabled') : t('admin.settings.disabled')}</dd>
            </dl>
          </>
        )}
      </section>

      <ConfirmModal
        open={disableConfirm}
        title={t('admin.settings.disable2faConfirmTitle')}
        variant="danger"
        message={t('admin.settings.disable2faConfirmMessage')}
        confirmLabel={t('admin.settings.disable2fa')}
        cancelLabel={t('admin.common.cancel')}
        onConfirm={() => disableMutation.mutate()}
        onCancel={() => setDisableConfirm(false)}
        loading={disableMutation.isPending}
      />
      <ConfirmModal
        open={!!removeDomain}
        title={t('admin.settings.removeDomainConfirmTitle')}
        variant="danger"
        message={removeDomain ? t('admin.settings.removeDomainConfirmMessage', { domain: removeDomain.domain }) : ''}
        confirmLabel={t('admin.settings.removeDomain')}
        cancelLabel={t('admin.common.cancel')}
        onConfirm={() => removeDomain && removeDomainMutation.mutate(removeDomain.id)}
        onCancel={() => setRemoveDomain(null)}
        loading={removeDomainMutation.isPending}
      />
    </div>
  )
}
